import { useMemo, useState } from 'react'
import type { DeploymentEvent } from '@/api/guardianforge/types'
import {
  DeploymentCard,
  DeploymentError,
  DeploymentJsonView,
  DeploymentSpinner,
  DeploymentStatusBadge,
} from '@/components/deployment/DeploymentUi'
import { useDeployApi } from '@/hooks/useDeployApi'
import { formatKstDateTime } from '@/utils/datetime'

const EVENT_TYPES = [
  'release_registered',
  'release_register_failed',
  'node_registered',
  'target_promoted',
  'target_rolled_back',
  'target_noop',
  'comparison_created',
  'comparison_succeeded',
  'comparison_failed',
  'decision_recorded',
]

export default function EventsPage() {
  const [eventType, setEventType] = useState('')
  const [nodeId, setNodeId] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [selected, setSelected] = useState<DeploymentEvent | null>(null)
  const events = useDeployApi<{ events: DeploymentEvent[] }>(
    '/deployments/events',
    {
      query: {
        event_type: eventType || undefined,
        node_id: nodeId || undefined,
        limit: 200,
      },
    },
    autoRefresh ? 10000 : null,
  )

  const filtered = useMemo(() => events.data?.events ?? [], [events.data])

  return (
    <>
      <h2>Events</h2>
      <p className="deployment-subtitle">감사 trail - release / comparison / decision 이벤트</p>

      <div className="deployment-toolbar">
        <select value={eventType} onChange={(event) => setEventType(event.target.value)}>
          <option value="">전체 type</option>
          {EVENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
        <input
          placeholder="node_id"
          value={nodeId}
          onChange={(event) => setNodeId(event.target.value)}
          style={{ width: 200 }}
        />
        <label className="deployment-flex" style={{ marginBottom: 0 }}>
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(event) => setAutoRefresh(event.target.checked)}
            style={{ width: 'auto' }}
          />
          <span className="deployment-muted">10초 자동 새로고침</span>
        </label>
        <button onClick={() => events.refresh()}>새로고침</button>
        {events.loading && <DeploymentSpinner />}
      </div>

      <DeploymentError error={events.error} />

      <div className="deployment-grid cols-2">
        <DeploymentCard title={`이벤트 (${filtered.length})`}>
          <div style={{ maxHeight: 540, overflow: 'auto' }}>
            <table className="deployment-table">
              <thead>
                <tr>
                  <th>시간</th>
                  <th>type</th>
                  <th>status</th>
                  <th>메시지</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((event) => (
                  <tr
                    key={event.event_id}
                    className={selected?.event_id === event.event_id ? 'selected' : 'clickable'}
                    onClick={() => setSelected(event)}
                  >
                    <td className="deployment-mono deployment-muted">{formatKstDateTime(event.created_at)}</td>
                    <td>{event.event_type}</td>
                    <td>
                      <DeploymentStatusBadge status={event.status} />
                    </td>
                    <td className="deployment-muted">{(event.message ?? '').slice(0, 80)}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={4} className="deployment-muted">
                      이벤트가 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </DeploymentCard>

        <DeploymentCard title="payload">
          {selected ? (
            <>
              <div className="deployment-muted">
                {selected.event_type} - {formatKstDateTime(selected.created_at)}
              </div>
              <DeploymentJsonView data={selected.payload} />
            </>
          ) : (
            <div className="deployment-muted">이벤트를 선택하세요.</div>
          )}
        </DeploymentCard>
      </div>
    </>
  )
}
